import type {
  AuthConfigResponse,
  AuthResponse,
  LoginInput,
  MeResponse,
  RegisterInput,
  WorkspaceBrief,
} from '@esportsbudget/shared';
import { useQueryClient } from '@tanstack/react-query';
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { changeLocale } from '@/i18n';
import {
  api,
  getActiveWorkspaceId,
  setAccessToken,
  setActiveWorkspaceId,
  setUnauthorizedHandler,
} from '@/lib/api';

/**
 * Состояние сессии.
 *
 * При старте приложения мы не знаем, залогинен ли пользователь: access-токен
 * живёт только в памяти вкладки. Поэтому первым делом дёргаем refresh по
 * httpOnly-cookie — если она валидна, пользователь входит молча.
 */

interface AuthContextValue {
  status: 'loading' | 'authenticated' | 'anonymous';
  me: MeResponse | null;
  config: AuthConfigResponse | null;
  workspace: WorkspaceBrief | null;
  workspaces: WorkspaceBrief[];

  login: (input: LoginInput) => Promise<void>;
  register: (input: RegisterInput) => Promise<AuthResponse>;
  logout: () => Promise<void>;
  refreshMe: () => Promise<MeResponse | null>;
  selectWorkspace: (workspaceId: string) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();

  const [status, setStatus] = useState<AuthContextValue['status']>('loading');
  const [me, setMe] = useState<MeResponse | null>(null);
  const [config, setConfig] = useState<AuthConfigResponse | null>(null);
  const [workspaceId, setWorkspaceIdState] = useState<string | null>(getActiveWorkspaceId());

  const bootstrapped = useRef(false);

  const applyMe = useCallback(
    (data: MeResponse | null) => {
      setMe(data);
      if (!data) {
        setStatus('anonymous');
        return;
      }
      setStatus('authenticated');

      // Язык интерфейса берём из профиля — он переезжает между устройствами.
      if (data.user.locale && data.user.locale !== i18n.language) {
        changeLocale(data.user.locale);
      }

      // Если сохранённый воркспейс пропал (удалён или исключили), берём первый.
      const stored = getActiveWorkspaceId();
      const exists = data.workspaces.some((workspace) => workspace.id === stored);
      const nextId = exists ? stored : (data.workspaces[0]?.id ?? null);
      setActiveWorkspaceId(nextId);
      setWorkspaceIdState(nextId);
    },
    [i18n],
  );

  const loadMe = useCallback(async (): Promise<MeResponse | null> => {
    try {
      const data = await api.get<MeResponse>('/auth/me');
      applyMe(data);
      return data;
    } catch {
      applyMe(null);
      return null;
    }
  }, [applyMe]);

  // Один раз при старте: конфигурация страницы входа + тихое восстановление сессии.
  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;

    void (async () => {
      try {
        setConfig(await api.get<AuthConfigResponse>('/auth/config', undefined, { skipRefresh: true }));
      } catch {
        setConfig(null);
      }

      try {
        const refreshed = await api.post<{ accessToken: string }>('/auth/refresh', undefined, {
          skipRefresh: true,
        });
        setAccessToken(refreshed.accessToken);
        await loadMe();
      } catch {
        applyMe(null);
      }
    })();
  }, [applyMe, loadMe]);

  // Когда сервер окончательно отказал в доступе — сбрасываем состояние.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setAccessToken(null);
      setMe(null);
      setStatus('anonymous');
      queryClient.clear();
    });
  }, [queryClient]);

  const login = useCallback(
    async (input: LoginInput) => {
      const result = await api.post<AuthResponse>('/auth/login', input, { skipRefresh: true });
      setAccessToken(result.accessToken);
      await loadMe();
    },
    [loadMe],
  );

  const register = useCallback(
    async (input: RegisterInput) => {
      const result = await api.post<AuthResponse>('/auth/register', input, { skipRefresh: true });
      setAccessToken(result.accessToken);
      await loadMe();
      return result;
    },
    [loadMe],
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout', undefined, { skipRefresh: true });
    } catch {
      /* даже если сервер недоступен, локально выходим */
    }
    setAccessToken(null);
    setActiveWorkspaceId(null);
    setWorkspaceIdState(null);
    setMe(null);
    setStatus('anonymous');
    queryClient.clear();
  }, [queryClient]);

  const selectWorkspace = useCallback(
    (id: string) => {
      setActiveWorkspaceId(id);
      setWorkspaceIdState(id);
      // Данные предыдущего воркспейса больше не актуальны.
      queryClient.clear();
    },
    [queryClient],
  );

  const workspace = useMemo(
    () => me?.workspaces.find((item) => item.id === workspaceId) ?? null,
    [me, workspaceId],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      me,
      config,
      workspace,
      workspaces: me?.workspaces ?? [],
      login,
      register,
      logout,
      refreshMe: loadMe,
      selectWorkspace,
    }),
    [status, me, config, workspace, login, register, logout, loadMe, selectWorkspace],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth должен использоваться внутри AuthProvider');
  return context;
}

/** Текущий воркспейс. Бросает ошибку, если вызван вне защищённой части приложения. */
export function useWorkspace(): WorkspaceBrief {
  const { workspace } = useAuth();
  if (!workspace) throw new Error('Нет активного воркспейса');
  return workspace;
}
